import React, { useState } from 'react';
import PropTypes from 'prop-types';

const FIELD_LABEL_MAP = {
  fully_executed_lease: 'Fully Executed Lease',
  commission_agreement: 'Commission Agreement',
  space_total_square_feet: 'Square Feet',
  building_total_square_feet: 'Square Footage',
};

const SQUARE_FOOTAGE_FIELDS = new Set(['space_total_square_feet', 'building_total_square_feet']);

// Fields backed by a dollar amount -- Term (lease_term_months) and Lease Escalation Percent
// (rent_escalation_percent) are decimals too but aren't currency, so they're excluded.
const CURRENCY_FIELDS = new Set([
  'base_rent',
  'tenant_improvement_allowance_amount_per_sqft',
  'rent_escalation_dollar',
  'operating_expenses_amount_per_sqft',
]);

const LEASE_ESCALATION_FIELDS = new Set(['lease_escalation', 'rent_escalation_is_custom']);
const isLeaseEscalationField = (fieldName, fieldLabel) => LEASE_ESCALATION_FIELDS.has(fieldName)
  || /^lease escalation(?:\s|$)/i.test(String(fieldLabel || '').trim());

const parseEscalationValue = (value) => {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value
      .replace(/Decimal\(['"](-?\d+(?:\.\d+)?)['"]\)/g, '$1')
      .replace(/'/g, '"')
      .replace(/\bNone\b/g, 'null')
      .replace(/\bTrue\b/g, 'true')
      .replace(/\bFalse\b/g, 'false'));
  } catch (error) {
    return value;
  }
};

const isTrue = value => value === true || String(value).toLowerCase() === 'true';

const formatEscalationRate = (value, isPercent) => {
  if (value === null || value === undefined || value === '') return '—';
  return isPercent
    ? `${formatChangeValue('rent_escalation_percent', value)}%`
    : formatChangeValue('rent_escalation_dollar', value);
};

export const formatCustomEscalationRow = (row, index) => {
  const isPercent = isTrue(row.is_percent);
  const rate = formatEscalationRate(isPercent ? row.rent_percent : row.rent_dollar, isPercent);
  const monthCount = Number(row.months);
  const hasDuration = Number.isFinite(monthCount) && row.months !== null && row.months !== '';
  const years = Math.floor(monthCount / 12);
  const months = monthCount % 12;
  const durationParts = [];
  if (years) durationParts.push(`${years} year${years === 1 ? '' : 's'}`);
  if (months || !years) durationParts.push(`${months} month${months === 1 ? '' : 's'}`);
  const duration = hasDuration ? ` (${durationParts.join(', ')})` : '';
  return `Rent ${index + 1}: ${rate}${duration}`;
};

export const getCustomEscalationRows = value => {
  const parsed = parseEscalationValue(value);
  return Array.isArray(parsed) ? parsed : null;
};

export const isFixedEscalationValue = (value) => {
  const parsed = parseEscalationValue(value);
  return Boolean(parsed && typeof parsed === 'object' && !Array.isArray(parsed));
};

export const formatLeaseEscalation = (value) => {
  const parsed = parseEscalationValue(value);
  if (typeof parsed === 'boolean') return parsed ? 'Custom' : 'Fixed';
  if (Array.isArray(parsed)) return 'Custom';
  if (!parsed || typeof parsed !== 'object') return '—';

  const isPercent = parsed.rent_escalation_is_percent !== undefined
    ? isTrue(parsed.rent_escalation_is_percent)
    : parsed.rent_escalation_percent !== null && parsed.rent_escalation_percent !== undefined;
  const amount = isPercent
    ? parsed.rent_escalation_percent
    : parsed.rent_escalation_dollar;
  return `Fixed: ${formatEscalationRate(amount, isPercent)}`;
};

const formatChangeValue = (fieldName, value, fieldLabel) => {
  if (value === null || value === undefined || value === '') return '—';
  if (isLeaseEscalationField(fieldName, fieldLabel)) return formatLeaseEscalation(value);
  const str = String(value).trim();

  if (fieldName === 'free_rent') {
    let totalMonths = null;
    if (typeof value === 'object') {
      const years = Number(value.years ?? 0);
      const months = Number(value.months ?? 0);
      totalMonths = Number.isFinite(years) && Number.isFinite(months) ? (years * 12) + months : null;
    } else if (typeof value === 'number' && Number.isFinite(value)) {
      totalMonths = Math.round(value);
    } else if (/^-?\d+(\.\d+)?$/.test(str)) {
      totalMonths = Math.round(Number(str));
    } else {
      const y = str.match(/years?['"]?\s*:\s*(-?\d+)/i);
      const m = str.match(/months?['"]?\s*:\s*(-?\d+)/i);
      if (y || m) totalMonths = (Number(y?.[1] || 0) * 12) + Number(m?.[1] || 0);
    }
    if (totalMonths === null) return str;
    return `${totalMonths} month${totalMonths !== 1 ? 's' : ''}`;
  }

  if (['date_lease_to', 'date_lease_from', 'date_lease_execution', 'date_lease_occupancy', 'lease_expiration_date'].includes(fieldName)) {
    const m = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return `${m[2]}/${m[3]}/${m[1]}`;
    const d = new Date(str);
    return Number.isNaN(d.getTime()) ? str : d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  }

  if (fieldName === 'fully_executed_lease' || fieldName === 'commission_agreement') {
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (/^true$/i.test(str)) return 'Yes';
    if (/^false$/i.test(str)) return 'No';
    return str;
  }

  // Whole number of months -- matches the live voucher flow's own Term field (integer, no decimals).
  if (fieldName === 'lease_term_months') {
    const n = Number(str);
    return Number.isFinite(n) ? String(Math.round(n)) : str;
  }

  if (SQUARE_FOOTAGE_FIELDS.has(fieldName)) {
    const n = Number(str.replace(/,/g, ''));
    return Number.isFinite(n) ? n.toLocaleString('en-US', { maximumFractionDigits: 3 }) : str;
  }

  // rent_escalation_percent displays at 3dp elsewhere in the app (RequiredLabeledItem.js's
  // 'percent3dp' format); every other decimal field here matches its 'currency' 2dp format.
  const decimalPlaces = fieldName === 'rent_escalation_percent' ? 3 : 2;
  const isCurrency = CURRENCY_FIELDS.has(fieldName);
  if (typeof value === 'number' && Number.isFinite(value) && !Number.isInteger(value)) {
    return `${isCurrency ? '$' : ''}${value.toFixed(decimalPlaces)}`;
  }
  if (/^-?\d+\.\d+$/.test(str)) {
    const n = Number(str);
    if (Number.isFinite(n)) return `${isCurrency ? '$' : ''}${n.toFixed(decimalPlaces)}`;
  }
  if (typeof value === 'object') return JSON.stringify(value);
  if (isCurrency && /^-?\d+$/.test(str)) return `$${Number(str).toFixed(decimalPlaces)}`;
  return str;
};

const getFieldLabel = change =>
  change.field_label || FIELD_LABEL_MAP[change.field_name] || change.field_name;

const FIELD_CHANGE_FILTERS = () => true;

// '—' and '-' are placeholder strings this file (and the backend snapshot) uses for "no value".
const EMPTY_PLACEHOLDERS = new Set(['', '—', '-']);
const hasValue = value => value !== null && value !== undefined && !EMPTY_PLACEHOLDERS.has(String(value).trim());

const AttachmentChange = ({ change }) => {
  const oldNames = change.old_attachment_names || [];
  const newNames = change.new_attachment_names || [];
  const addedNames = newNames.filter(name => !oldNames.includes(name));
  const removedNames = oldNames.filter(name => !newNames.includes(name));

  return (
    <>
      {addedNames.length > 0 && (
        <li>
          {'Added '}
          <strong>{getFieldLabel(change)}</strong>
          :
          <ul>
            {addedNames.map(name => <li key={`selected-${name}`}>{name}</li>)}
          </ul>
        </li>
      )}
      {removedNames.length > 0 && (
        <li>
          {'Removed '}
          <strong>{getFieldLabel(change)}</strong>
          :
          <ul>
            {removedNames.map(name => <li key={`removed-${name}`}>{name}</li>)}
          </ul>
        </li>
      )}
      {removedNames.length > 0 && newNames.length > 0 && (
        <li>
          {'Remaining '}
          <strong>{getFieldLabel(change)}</strong>
          :
          <ul>
            {newNames.map(name => <li key={`remaining-${name}`}>{name}</li>)}
          </ul>
        </li>
      )}
      {addedNames.length === 0 && removedNames.length === 0 && newNames.length > 0 && (
        <li>
          <strong>{getFieldLabel(change)}</strong>
          :
          <ul>
            {newNames.map(name => <li key={`selected-${name}`}>{name}</li>)}
          </ul>
        </li>
      )}
    </>
  );
};

AttachmentChange.propTypes = {
  change: PropTypes.shape({
    field_label: PropTypes.string,
    old_value: PropTypes.node,
    new_value: PropTypes.node,
    old_attachment_names: PropTypes.arrayOf(PropTypes.string),
    new_attachment_names: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};

const VoucherVerificationNotification = ({ relatedObject, performedBy }) => {
  const [expanded, setExpanded] = useState(false);

  const verifierName = relatedObject?.verifier_name
    || `${performedBy?.first_name || ''} ${performedBy?.last_name || ''}`.trim()
    || 'A verifier';
  const dealName = relatedObject?.deal_name || 'the deal';

  // summary: only the final verify-action changes
  const summaryChanges = (relatedObject?.changes_summary || []).filter(c => c.field_name !== 'comment'
    && FIELD_CHANGE_FILTERS(c.field_name)
    && (c.field_name !== 'attachments'
      || (c.old_attachment_names?.length > 0 || c.new_attachment_names?.length > 0))
    && !(formatChangeValue(c.field_name, c.old_value, c.field_label) === '—'
      && formatChangeValue(c.field_name, c.new_value, c.field_label) === '—'));

  const hasUpdates = summaryChanges.length > 0;

  const PREVIEW_LIMIT = 3;
  const allItems = [
    ...summaryChanges.map(change => ({ type: 'change', change })),
  ];
  const hasMore = allItems.length > PREVIEW_LIMIT;
  const visibleItems = expanded ? allItems : allItems.slice(0, PREVIEW_LIMIT);

  return (
    <div className="voucher-verification-notification">
      <p>
        <strong>{verifierName}</strong>
        {' validated voucher for '}
        <strong>{dealName}</strong>
        {hasUpdates ? ' and made the following updates:' : '.'}
      </p>

      {hasUpdates && (
        <ul className="verification-changes-list">
          {visibleItems.map((item) => {
            const { change } = item;
            if (change.field_name === 'attachments') {
              return <AttachmentChange key="attachments" change={change} />;
            }
            const changeKey = `${change.field_name}-${String(change.old_value)}-${String(change.new_value)}`;
            const formattedOldValue = formatChangeValue(change.field_name, change.old_value, change.field_label);
            const formattedNewValue = formatChangeValue(change.field_name, change.new_value, change.field_label);
            const isEscalation = isLeaseEscalationField(change.field_name, change.field_label);
            const isAddition = isEscalation
              ? formattedOldValue === '—' && formattedNewValue !== '—'
              : !hasValue(change.old_value) && hasValue(change.new_value);
            const isFixedToFixed = isEscalation
              && isFixedEscalationValue(change.old_value)
              && isFixedEscalationValue(change.new_value);
            const isFixedAddition = isAddition
              && isEscalation
              && isFixedEscalationValue(change.new_value);
            const oldCustomRows = isEscalation ? getCustomEscalationRows(change.old_value) : null;
            const customRows = isEscalation ? getCustomEscalationRows(change.new_value) : null;
            if (customRows) {
              const renderScheduleRows = (rows, keyPrefix) => (rows.length > 0
                ? (
                  <ul>
                    {rows.map((row, index) => (
                      <li key={`${keyPrefix}-${index}`}>{formatCustomEscalationRow(row, index)}</li>
                    ))}
                  </ul>
                )
                : <span>: No rent rows</span>);

              return (
                <li key={changeKey}>
                  {oldCustomRows ? (
                    <>
                      {'Changed '}
                      <strong>{getFieldLabel(change)} (Custom)</strong>
                      {':'}
                    </>
                  ) : isAddition ? (
                    <>
                      {'Added '}
                      <strong>{getFieldLabel(change)} (Custom)</strong>
                      {':'}
                    </>
                  ) : (
                    <>
                      {'Changed '}
                      <strong>{getFieldLabel(change)}</strong>
                      {' from '}
                      <strong>{formattedOldValue}</strong>
                      {' to '}
                      <strong>Custom</strong>
                      {':'}
                    </>
                  )}
                  {oldCustomRows ? (
                    <ul>
                      <li>
                        <strong>Previous rent schedule</strong>
                        {renderScheduleRows(oldCustomRows, 'previous-rent')}
                      </li>
                      <li>
                        <strong>Updated rent schedule</strong>
                        {renderScheduleRows(customRows, 'updated-rent')}
                      </li>
                    </ul>
                  ) : customRows.length > 0 && renderScheduleRows(customRows, 'rent')}
                </li>
              );
            }
            return (
              <li key={changeKey}>
                {isFixedToFixed ? (
                  <>
                    {'Changed '}
                    <strong>{getFieldLabel(change)} (Fixed)</strong>
                    {': from '}
                    <strong>{formattedOldValue.replace(/^Fixed:\s*/, '')}</strong>
                    {' to '}
                    <strong>{formattedNewValue.replace(/^Fixed:\s*/, '')}</strong>
                    .
                  </>
                ) : isFixedAddition ? (
                  <>
                    {'Added '}
                    <strong>{getFieldLabel(change)} (Fixed)</strong>
                    {': '}
                    <strong>{formattedNewValue.replace(/^Fixed:\s*/, '')}</strong>
                    .
                  </>
                ) : isAddition ? (
                  <>
                    {'Added '}
                    <strong>{getFieldLabel(change)}</strong>
                    {': '}
                    <strong>{formattedNewValue}</strong>
                    .
                  </>
                ) : (
                  <>
                    {'Changed '}
                    <strong>{getFieldLabel(change)}</strong>
                    {' from '}
                    <strong>{formattedOldValue}</strong>
                    {' to '}
                    <strong>{formattedNewValue}</strong>
                    .
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {hasMore && (
        <button
          type="button"
          className="verification-history-toggle"
          onClick={() => setExpanded(prev => !prev)}
        >
          {expanded ? 'Show less' : `Show all ${allItems.length} updates`}
        </button>
      )}

    </div>
  );
};

export default VoucherVerificationNotification;

VoucherVerificationNotification.propTypes = {
  relatedObject: PropTypes.shape({
    deal_id: PropTypes.number,
    deal_name: PropTypes.string,
    verifier_name: PropTypes.string,
    changes_summary: PropTypes.arrayOf(PropTypes.shape({})),
    audit_details: PropTypes.shape({
      attachments: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  }),
  performedBy: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
  }),
};

VoucherVerificationNotification.defaultProps = {
  relatedObject: {},
  performedBy: {},
};
